import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  output,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { PageNavigationService } from '../../service/app-state/page-navigation.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import {
  ct2ConfigurationByBatchDomain,
  FieldModel,
} from '../../models/extraction-models/extraction-models';
import { FormControlStatus, ReactiveFormsModule } from '@angular/forms';
import { TableExtractionViewComponent } from '../../domains/data-capturing/table-extraction-view-component/table-extraction-view.component';
import { FieldGroupExtractionViewComponent } from '../../domains/data-capturing/field-group-extraction-view-component/field-group-extraction-view.component';
import { ConsolidationInitialDataProvider } from '../../domains/data-capturing/extraction-data-provider';
import {
  ExtractedRow,
  ExtractedTablesByKey,
  ExtractedValuesByKey,
} from '../../models/extracted-values';
import { PersistDataService } from '../../service/app-state/persist-data.service';
import { PrefillService } from '../../service/prefill.service';
import { debounceTime } from 'rxjs';
import {
  ConsolidationInputStylingService,
  INPUT_STYLING_SERVICE,
} from '../../service/input-styling.service';
import { ExtractedDataStoreService } from '../../service/extracted-data/extracted-data-store.service';
import { ExtractionField } from '../extraction-page/extraction-page.component';
import { OcrFormInputDirective } from '../../directives/ocr-form-input.directive';
import {
  ExtractionPage,
  ExtractionSection,
  isFieldGroupModel,
  isTableModel,
} from '../../models/domain-extraction-specifications';

@Component({
  selector: 'ct-extraction-consolidation',
  templateUrl: './consolidation.component.html',
  providers: [{ provide: INPUT_STYLING_SERVICE, useClass: ConsolidationInputStylingService }],
  imports: [
    ReactiveFormsModule,
    TableExtractionViewComponent,
    FieldGroupExtractionViewComponent,
    OcrFormInputDirective,
  ],
})
export class ConsolidationComponent {
  private readonly pageNavigationService = inject(PageNavigationService);
  private readonly initialDataProvider = inject(ConsolidationInitialDataProvider);
  private readonly prefillService = inject(PrefillService);
  private readonly persistDataStoreService = inject(PersistDataService);
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);

  protected readonly ctBatchStore = inject(CtBatchStoreService);

  protected readonly config = ct2ConfigurationByBatchDomain[this.ctBatchStore.ctBatchDomain];

  private ignoreTableEvents = true;

  readonly splitDocumentId = input<string>();
  readonly splitEntityIndex = input.required<number>();
  readonly extractionPage = input.required<ExtractionPage>();
  readonly sections = computed(() =>
    (this.extractionPage()?.sections ?? []).filter(
      (section) =>
        (!section.visibleForSource ||
          section.visibleForSource.includes(this.ctBatchStore.ctBachSource)) &&
        section.invisibleInConsolidation !== true,
    ),
  );
  readonly currentActiveField = model.required<ExtractionField | undefined>();
  readonly currentActiveSection = model.required<ExtractionSection | undefined>();
  readonly currentActiveFieldModel = input.required<FieldModel | undefined>();
  readonly areAllSectionsValid = input.required<boolean>();

  readonly currentActiveSectionInComponent = signal<ExtractionSection | undefined>(undefined);

  readonly focusNextField = output();
  readonly focusNextSection = output();
  readonly changeSectionStatus = output<{
    section: ExtractionSection;
    status: FormControlStatus;
  }>();
  readonly continue = output();

  readonly tableExtractionView = viewChild(TableExtractionViewComponent);

  protected readonly prefillResults = computed(() =>
    this.prefillService.applyTo(this.initialDataProvider.getInitialData(this.splitDocumentId())),
  );

  protected readonly consolidatedData = linkedSignal(() => ({
    fields: this.prefillResults().fields,
    tables: {} as ExtractedTablesByKey,
  }));

  initialTableRowsByKeyResource = resource({
    params: () => ({ splitEntityIndex: this.splitEntityIndex() }),
    loader: async ({ params: { splitEntityIndex } }) => {
      // TODO: this is basically pseudo-code. Check if this works as intended and refactor...
      const consolidatedDocuments = this.ctBatchStore.ctBatch.documents.filter((document) =>
        document.documentClasses.some((documentClass) =>
          this.config.consolidation.consolidatedDocumentClasses.includes(documentClass),
        ),
      );

      const tableDataByDocumentId: Record<string, Record<string, ExtractedRow[]>> = {};
      for (const document of consolidatedDocuments) {
        tableDataByDocumentId[document.id] =
          await this.extractedDataStoreService.getExtractedTables(document.id);
      }

      const result: Record<string, ExtractedRow[]> = {};
      const splitScopedTableKeys = this.config.batchSplitConfiguration?.splitScopedTableKeys ?? [];

      Object.values(tableDataByDocumentId).forEach((tableData) => {
        Object.entries(tableData).forEach(([key, rows]) => {
          if (splitScopedTableKeys.includes(key)) return;
          if (!(key in result)) result[key] = [];
          result[key] = [...result[key], ...rows];
        });
      });

      const splitDocuments = consolidatedDocuments.filter((document) =>
        document.documentClasses.includes(
          this.config.batchSplitConfiguration?.splitInducingDocumentClass,
        ),
      );

      const splitDocumentId: string | undefined = splitDocuments[splitEntityIndex]?.id;
      if (splitDocumentId) {
        for (const key of splitScopedTableKeys) {
          const rows = tableDataByDocumentId[splitDocumentId]?.[key] ?? [];
          if (!(key in result)) result[key] = [];
          result[key] = [...result[key], ...rows];
        }
      }

      return result;
    },
  });

  private readonly host = inject(ElementRef<HTMLElement>);

  constructor() {
    effect(() => {
      if (this.areAllSectionsValid()) {
        this.pageNavigationService.enableNavigation('continue');
      } else {
        this.pageNavigationService.disableNavigation(
          'continue',
          'Bitte vervollständige bzw. korrigiere deine Eingaben',
        );
      }
    });

    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.continue.emit());

    toObservable(this.consolidatedData)
      .pipe(takeUntilDestroyed(), debounceTime(1000))
      .subscribe(({ fields, tables }) => this.saveConsolidationSnapshot(fields, tables));

    effect(() => {
      this.ignoreTableEvents = true;
      this.splitEntityIndex();

      // do some stuff after a little timeout
      setTimeout(() => {
        this.host.nativeElement.parentElement?.scrollTo({ top: 0 });
        this.ignoreTableEvents = false;
      }, 50);
    });
  }

  private async saveConsolidationSnapshot(
    fields: Partial<ExtractedValuesByKey>,
    tables: ExtractedTablesByKey,
  ) {
    const data = await this.persistDataStoreService.getConsolidatedDataSnapshot(
      this.splitEntityIndex(),
    );
    await this.persistDataStoreService.saveConsolidatedDataSnapshot(
      this.splitEntityIndex(),
      { ...data.fields, ...fields },
      { ...data.tables, ...tables },
    );
  }

  saveFieldGroupData(values: ExtractedValuesByKey) {
    this.consolidatedData.update((data) => ({
      ...data,
      fields: { ...data.fields, ...values },
    }));
  }

  saveTableValues(key: string, rows: ExtractedRow[]) {
    this.consolidatedData.update((data) => ({
      ...data,
      tables: { ...data.tables, [key]: rows },
    }));
  }

  onCurrentFormFieldChange(section: ExtractionSection, field?: ExtractionField) {
    this.currentActiveSection.set(section);
    this.currentActiveField.set(field);
    this.currentActiveSectionInComponent.set(undefined);
    this.tableExtractionView()?.currentTableFieldIndex.set(undefined);
  }

  onCurrentTableFieldChange(field: ExtractionField | undefined, section: ExtractionSection) {
    if (this.ignoreTableEvents) {
      return;
    }
    this.currentActiveSectionInComponent.set(section);
    this.currentActiveSection.set(section);

    if (field) {
      this.currentActiveField.set(field);
    } else {
      this.currentActiveField.set({
        model: section.fieldModels[0],
      });
    }
  }

  handleExtractionContinue() {
    if (!this.currentActiveSection()) {
      return;
    }

    const nextSection = this.extractionPage().sections[this.currentActiveSection()!.index + 1];

    const lastFieldBeforeTableModel = (
      this.extractionPage().sections.filter(
        (section) => section.type !== 'tableModel',
      ) as ExtractionSection[]
    )
      .at(-1)
      ?.fieldModels.at(-1);

    const currentFieldIsLastBeforeTableModel =
      lastFieldBeforeTableModel?.key === this.currentActiveField()?.model.key;

    if (currentFieldIsLastBeforeTableModel) {
      this.currentActiveSection.set(undefined);
      this.currentActiveField.set(undefined);

      // without this - we also trigger the ENTER on the table and jump to index 1 instead 0
      setTimeout(() => {
        this.currentActiveSectionInComponent.set(nextSection);
        this.tableExtractionView()?.currentTableFieldIndex.set(0);
      }, 10);

      return;
    }

    this.focusNextField.emit();
  }

  protected readonly isFieldGroupModel = isFieldGroupModel;
  protected readonly isTableModel = isTableModel;
}
